import * as THREE from 'three'
import type { Texture as TextureDef } from './mapdescriptor'

// All textures are generated at boot from a <canvas>. No assets ever leave this file.

const PROCEDURAL_PREFIX = 'procedural:'

// Resolves a MapDescriptor texture entry to a Three.js texture. Owns the
// `procedural:<kind>:<args>` URL grammar; any other URL falls back to a
// hot-pink placeholder until real image loading lands.
export function resolveTexture(def: TextureDef): THREE.Texture {
  if (def.url.startsWith(PROCEDURAL_PREFIX)) {
    return makeProceduralTexture(def.url)
  }
  return makePlaceholderTexture()
}

function makeProceduralTexture(url: string): THREE.CanvasTexture {
  const parts = url.slice(PROCEDURAL_PREFIX.length).split(':')
  const kind = parts[0]
  if (kind === 'grid') {
    const base = parts[1] ?? '#3a4658'
    const line = parts[2] ?? '#1a212c'
    const cells = parts[3] ? parseInt(parts[3], 10) : 8
    return makeGridTexture(base, line, cells)
  }
  if (kind === 'stripe') {
    const base = parts[1] ?? '#c34660'
    const stripe = parts[2] ?? '#5b1626'
    const bands = parts[3] ? parseInt(parts[3], 10) : 14
    return makeStripeTexture(base, stripe, bands)
  }
  throw new Error(`Unknown procedural texture kind: ${kind}`)
}

export function makePlaceholderTexture(): THREE.CanvasTexture {
  const size = 16
  const c = document.createElement('canvas')
  c.width = c.height = size
  const ctx = c.getContext('2d')!
  ctx.fillStyle = '#ff00ff'
  ctx.fillRect(0, 0, size, size)
  const tex = new THREE.CanvasTexture(c)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  tex.magFilter = THREE.NearestFilter
  tex.minFilter = THREE.NearestFilter
  return tex
}

export function makeSkyTexture(): THREE.CanvasTexture {
  const w = 2048, h = 1024
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  const ctx = c.getContext('2d')!

  // Night-to-dusk gradient (top = deep space, bottom = horizon haze)
  const grad = ctx.createLinearGradient(0, 0, 0, h)
  grad.addColorStop(0.00, '#06081e')
  grad.addColorStop(0.45, '#121640')
  grad.addColorStop(0.85, '#3a4670')
  grad.addColorStop(1.00, '#7a8aa8')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, w, h)

  // Faint nebula clouds
  for (let i = 0; i < 6; i++) {
    const x = Math.random() * w
    const y = Math.random() * h * 0.6
    const r = 180 + Math.random() * 260
    const g2 = ctx.createRadialGradient(x, y, 0, x, y, r)
    const hue = 220 + Math.random() * 60
    g2.addColorStop(0, `hsla(${hue},60%,55%,0.07)`)
    g2.addColorStop(1, 'hsla(0,0%,0%,0)')
    ctx.fillStyle = g2
    ctx.fillRect(x - r, y - r, r * 2, r * 2)
  }

  // Small stars (denser toward zenith)
  for (let i = 0; i < 1800; i++) {
    const x = Math.random() * w
    const y = Math.pow(Math.random(), 1.4) * (h * 0.92)
    const r = Math.random() * 1.2 + 0.25
    const a = Math.random() * 0.7 + 0.3
    const tint = 215 + Math.floor(Math.random() * 40)
    ctx.fillStyle = `rgba(255,${tint + 5},${tint},${a})`
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }

  // A few bright stars with halos
  for (let i = 0; i < 35; i++) {
    const x = Math.random() * w
    const y = Math.pow(Math.random(), 1.6) * (h * 0.7)
    const r = Math.random() * 1.6 + 1.4
    const g3 = ctx.createRadialGradient(x, y, 0, x, y, r * 5)
    g3.addColorStop(0, 'rgba(255,255,240,0.55)')
    g3.addColorStop(1, 'rgba(255,255,240,0)')
    ctx.fillStyle = g3
    ctx.fillRect(x - r * 5, y - r * 5, r * 10, r * 10)
    ctx.fillStyle = 'rgba(255,255,245,1)'
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }

  // Moon
  const mx = w * 0.78, my = h * 0.18, mr = 46
  const mglow = ctx.createRadialGradient(mx, my, 0, mx, my, mr * 4)
  mglow.addColorStop(0, 'rgba(245,238,210,0.45)')
  mglow.addColorStop(1, 'rgba(245,238,210,0)')
  ctx.fillStyle = mglow
  ctx.fillRect(mx - mr * 4, my - mr * 4, mr * 8, mr * 8)
  ctx.fillStyle = '#f6efd0'
  ctx.beginPath()
  ctx.arc(mx, my, mr, 0, Math.PI * 2)
  ctx.fill()
  // Crater shading
  ctx.fillStyle = 'rgba(120,100,70,0.15)'
  for (let i = 0; i < 6; i++) {
    const dx = (Math.random() - 0.5) * mr
    const dy = (Math.random() - 0.5) * mr
    if (Math.hypot(dx, dy) > mr * 0.7) continue
    ctx.beginPath()
    ctx.arc(mx + dx, my + dy, 3 + Math.random() * 6, 0, Math.PI * 2)
    ctx.fill()
  }

  const tex = new THREE.CanvasTexture(c)
  tex.mapping = THREE.EquirectangularReflectionMapping
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 4
  return tex
}

// Stripe texture: bands perpendicular to V axis. When applied to a ramp's top face
// with V aligned to motion axis, the player sees bands fly past = strong motion cue.
export function makeStripeTexture(base: string, stripe: string, bands = 8): THREE.CanvasTexture {
  const size = 256
  const c = document.createElement('canvas')
  c.width = c.height = size
  const ctx = c.getContext('2d')!

  ctx.fillStyle = base
  ctx.fillRect(0, 0, size, size)

  const period = size / bands
  const bandThickness = Math.max(3, Math.floor(period * 0.18))
  ctx.fillStyle = stripe
  for (let y = 0; y < size; y += period) {
    ctx.fillRect(0, y, size, bandThickness)
  }

  // Bright leading edge gives crisper motion read
  ctx.fillStyle = 'rgba(255,255,255,0.12)'
  for (let y = 0; y < size; y += period) {
    ctx.fillRect(0, y, size, 1)
  }
  // Subtle warning chevrons (small triangles between bands)
  ctx.fillStyle = 'rgba(0,0,0,0.18)'
  for (let y = period * 0.55; y < size; y += period) {
    const cx = size / 2
    const r = period * 0.2
    ctx.beginPath()
    ctx.moveTo(cx - r, y)
    ctx.lineTo(cx + r, y)
    ctx.lineTo(cx, y + r)
    ctx.closePath()
    ctx.fill()
  }

  const tex = new THREE.CanvasTexture(c)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}

export function makeGridTexture(base: string, line: string, cells = 8): THREE.CanvasTexture {
  const size = 256
  const c = document.createElement('canvas')
  c.width = c.height = size
  const ctx = c.getContext('2d')!

  ctx.fillStyle = base
  ctx.fillRect(0, 0, size, size)

  const step = size / cells
  ctx.strokeStyle = line
  ctx.lineWidth = 2
  for (let i = 0; i <= cells; i++) {
    ctx.beginPath(); ctx.moveTo(i * step, 0); ctx.lineTo(i * step, size); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(0, i * step); ctx.lineTo(size, i * step); ctx.stroke()
  }

  // Faint diagonal hashes inside each cell for extra parallax texture
  ctx.strokeStyle = 'rgba(255,255,255,0.05)'
  ctx.lineWidth = 1
  for (let i = 0; i < cells; i++) {
    for (let j = 0; j < cells; j++) {
      const x = i * step, y = j * step
      ctx.beginPath()
      ctx.moveTo(x + 2, y + step - 2)
      ctx.lineTo(x + step - 2, y + 2)
      ctx.stroke()
    }
  }

  const tex = new THREE.CanvasTexture(c)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  return tex
}
